import { useMemo } from 'react';
import { useMediaResource } from '@brightmls/nextgen.lae.flc';
import { useMediaStore } from '@brightmls/nextgen.lae.mediacenter';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../flags';
import { MediaCategory } from '../../../system/media';
import { MediaField } from '../../../system/field';
import { toHTTPSURL } from '../../../utils';

interface PrimaryPhotoResult {
  /** The primary photo media record, or null if no photos exist */
  primaryPhoto: any | null;
  /** HTTPS URL for the primary photo (medium size preferred, falls back to full URL) */
  primaryPhotoUrl: string | null;
}

/**
 * React hook that returns the primary (main) photo for a listing.
 *
 * Supports both MediaV1 (legacy) and MediaV2 (media center) modes:
 * - MediaV1: Uses useMediaResource from FLC to get photos
 * - MediaV2: Uses useMediaStore from the media center library when hydrated,
 *            falls back to FLC if not yet hydrated
 *
 * The primary photo is determined by the lowest MEDIA_DISPLAY_ORDER value.
 */
export const usePrimaryPhoto = (): PrimaryPhotoResult => {
  const { hasFeature } = useFeatureFlags();
  const isMediaV2 = hasFeature(FeatureFlags.NGW_MEDIA_V2);

  // MediaV1: Get photos from FLC (also used as fallback for MediaV2)
  const { media: flcPhotos } = useMediaResource(MediaCategory.Photo);

  // MediaV2: Get items and hydration state from media center store
  const mediaCenterItems = useMediaStore((state) => state.items);
  const isHydrated = useMediaStore((state) => state.isHydrated);

  const primaryPhoto = useMemo(() => {
    // Use media center store only if MediaV2 is enabled AND store is hydrated
    if (isMediaV2 && isHydrated) {
      // MediaV2: Filter to photos only and find primary
      const photos = mediaCenterItems.filter(
        (item) => item.MEDIA_CATEGORY === 'Photo',
      );

      if (photos.length === 0) {
        return null;
      }

      // Find photo with lowest MEDIA_DISPLAY_ORDER
      return photos.reduce((prev, curr) =>
        curr.MEDIA_DISPLAY_ORDER < prev.MEDIA_DISPLAY_ORDER ? curr : prev,
      );
    } else {
      // MediaV1 or MediaV2 not yet hydrated: Use FLC photos
      if (!flcPhotos || flcPhotos.length === 0) {
        return null;
      }

      // Sort by MEDIA_DISPLAY_ORDER and return first
      const sorted = [...flcPhotos].sort(
        (photoA: any, photoB: any) =>
          photoA[MediaField.MEDIA_DISPLAY_ORDER] -
          photoB[MediaField.MEDIA_DISPLAY_ORDER],
      );

      return sorted[0];
    }
  }, [isMediaV2, isHydrated, mediaCenterItems, flcPhotos]);

  const primaryPhotoUrl = useMemo(() => {
    if (!primaryPhoto) {
      return null;
    }

    // Prefer medium URL, fall back to full URL, then local preview for processing items
    const url =
      primaryPhoto[MediaField.MEDIA_URL_MEDIUM] ||
      primaryPhoto[MediaField.MEDIA_URL] ||
      primaryPhoto._processedUrl ||
      primaryPhoto._localPreviewUrl;

    if (!url) return null;

    // blob: and data: URLs are local previews — return as-is without HTTPS conversion
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }

    return toHTTPSURL(url) || null;
  }, [primaryPhoto]);

  return {
    primaryPhoto,
    primaryPhotoUrl,
  };
};
